/**
 * Domain Entity: User
 * Represents the core user entity with business logic
 */
export class User {
  private constructor(
      private readonly _id: string,
      private readonly _email: string,
      private readonly _firstName: string,
      private readonly _lastName: string,
      private readonly _type: 'swimmer' | 'coach',
      private readonly _createdAt: Date = new Date(),
      private readonly _updatedAt: Date = new Date(),
      private readonly _image: string
  ) {}

  static create(
    id: string,
    email: string,
    firstName: string,
    lastName: string,
    type: 'swimmer' | 'coach',
    image: string,
  ): User {
    if (!id || !email || !firstName || !lastName) {
      throw new Error('All user fields are required');
    }

    if (!this.isValidEmail(email)) {
      throw new Error('Invalid email format');
    }

    return new User(id, email, firstName, lastName, type, new Date(), new Date(), image);
  }

  static fromData(data: {
  id: string;
  email: string;
  fname: string;
  lname: string;
  type: 'swimmer' | 'coach';
  created_at?: string;
  updated_at?: string;
  image?: string;
}): User {
  return new User(
    data.id,
    data.email,
    data.fname,
    data.lname,
    data.type,
    data.created_at ? new Date(data.created_at) : new Date(),
    data.updated_at ? new Date(data.updated_at) : new Date(),
    data.image ?? ""
  );
}

  get id(): string {
    return this._id;
  }

  get email(): string {
    return this._email;
  }

  get firstName(): string {
    return this._firstName;
  }

  get lastName(): string {
    return this._lastName;
  }

  get fullName(): string {
    return `${this._firstName} ${this._lastName}`;
  }

  get initials(): string {
    return `${this._firstName[0]}${this._lastName[0]}`.toUpperCase();
  }

  get type(): 'swimmer' | 'coach' {
    return this._type;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  get image(): string {
    return `${this._image}`;
  }

  isCoach(): boolean {
    return this._type === 'coach';
  }

  isSwimmer(): boolean {
    return this._type === 'swimmer';
  }

  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  toJSON() {
    return {
      id: this._id,
      email: this._email,
      fname: this._firstName,
      lname: this._lastName,
      type: this._type,
      image: this._image,
      created_at: this._createdAt.toISOString(),
      updated_at: this._updatedAt.toISOString()
    };
  }
}
